/**
 * Regions from themes (TD §7.1 "Change from the prototype"): every pick belongs to one chosen theme, themes get
 * stretches of the route in the order chosen, and the aquatic or wetland theme is the one rotated next to water.
 */
import type { Species, Theme, ThemeId } from '../../shared/types';

/** Which region every pick belongs to, the regions in walking order, and the region to put by the water. */
export interface Grouping {
  groupOf: number[];
  groups: number[];
  waterGroup: number | null;
}

const isWet = (t: Theme) => t.biome === 'aquatic' || t.habitatTypes.includes('wetland');

/** How well a species fits a theme it isn't suggested by: biome, habitat type and region. */
function fit(s: Species, t: Theme): number {
  return (s.biome === t.biome ? 2 : 0) + (s.habitatType && t.habitatTypes.includes(s.habitatType) ? 2 : 0)
    + (s.regions.some(r => t.regions.includes(r)) ? 1 : 0);
}

/**
 * Assigns picks to the chosen themes: the first chosen theme that suggests the species, else the best fit (ties
 * go to the earlier theme). Without any known theme, picks are grouped by biome as in the prototype.
 * `waterNeed` is the needed water per pick (m²); it decides which wet region goes by the water.
 */
export function groupPicks(picks: readonly Species[], waterNeed: readonly number[], themeIds: readonly ThemeId[], themes: readonly Theme[]): Grouping {
  const chosen = themeIds.flatMap(id => themes.filter(t => t.id === id).slice(0, 1));
  let groupOf: number[], wet: (g: number) => boolean;
  if (chosen.length) {
    groupOf = picks.map(s => {
      const sug = chosen.findIndex(t => t.suggested.includes(s.id));
      if (sug >= 0) return sug;
      let best = 0, bv = -1;
      chosen.forEach((t, i) => { const v = fit(s, t); if (v > bv) { bv = v; best = i; } });
      return best;
    });
    wet = g => isWet(chosen[g] as Theme);
  } else {
    const biomes: string[] = [];
    groupOf = picks.map(s => { let i = biomes.indexOf(s.biome); if (i < 0) { i = biomes.length; biomes.push(s.biome); } return i; });
    wet = g => biomes[g] === 'aquatic';
  }
  const groups = [...new Set(groupOf)].sort((a, b) => a - b);
  let waterGroup: number | null = null, bestW = -1;
  for (const g of groups) {
    if (!wet(g)) continue;
    const w = picks.reduce((s, _, i) => s + (groupOf[i] === g ? waterNeed[i] ?? 0 : 0), 0);
    if (w > bestW) { bestW = w; waterGroup = g; }
  }
  return { groupOf, groups, waterGroup };
}
